import React, { useState, useEffect, useRef } from "react";
import { MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { registerFlush } from "@/lib/flushRegistry";

const MAX = 300;

export const ToelichtingKnop = ({ taskId, open, heeft, onClick }) => (
  <button type="button" data-testid={`toelichting-knop-${taskId}`} aria-label="Toelichting toevoegen" aria-expanded={open} onClick={onClick}
    className={`shrink-0 h-11 w-11 sm:h-8 sm:w-8 mt-0.5 mr-1 flex items-center justify-center rounded-md hover:bg-zinc-100 transition-colors ${heeft ? "text-blue-700" : "text-zinc-400"}`}>
    <MessageSquarePlus className="h-4 w-4" />
  </button>
);

// Remount per week/persoon/taak (key): de cleanup schrijft altijd naar de week/persoon waarvoor de tekst is getypt.
export function ToelichtingRegel({ week, om, taskId, waarde, editable, open, onSaved }) {
  const [tekst, setTekst] = useState(waarde || "");
  const tekstRef = useRef(tekst);
  const opgeslagen = useRef((waarde || "").trim());
  const timer = useRef(null);
  const levend = useRef(true);
  tekstRef.current = tekst;

  const bewaar = async () => {
    clearTimeout(timer.current);
    const t = tekstRef.current.trim();
    if (t === opgeslagen.current || t.length > MAX) return;
    try {
      await api.put("/weekritme/toelichting", { week, om, taskId, toelichting: t });
      opgeslagen.current = t;
      if (onSaved) onSaved(taskId, t, week, om);   // v18-R1 R1: ook na unmount (dag ingeklapt); de pagina checkt week/persoon
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toelichting opslaan mislukt. Je tekst staat nog in het vak.");
    }
  };
  const bewaarRef = useRef(bewaar);
  bewaarRef.current = bewaar;
  useEffect(() => {
    levend.current = true;
    const tabDicht = () => {
      const t = tekstRef.current.trim();
      if (t === opgeslagen.current || t.length > MAX) return;
      opgeslagen.current = t;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/weekritme/toelichting`, {
        method: "PUT", keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("tw1_token")}` },
        body: JSON.stringify({ week, om, taskId, toelichting: t }),
      }).catch(() => {});
    };
    const verborgen = () => { if (document.visibilityState === "hidden") tabDicht(); };
    window.addEventListener("beforeunload", tabDicht);
    window.addEventListener("pagehide", tabDicht);
    document.addEventListener("visibilitychange", verborgen);
    const afmelden = registerFlush(() => bewaarRef.current());   // v17-R1 D4
    return () => {
      afmelden();
      window.removeEventListener("beforeunload", tabDicht); window.removeEventListener("pagehide", tabDicht);
      document.removeEventListener("visibilitychange", verborgen);
      levend.current = false; clearTimeout(timer.current); bewaarRef.current();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wijzig = (v) => { setTekst(v); clearTimeout(timer.current); timer.current = setTimeout(() => bewaarRef.current(), 1600); };
  if (!editable) return waarde ? <p data-testid={`toelichting-${taskId}`} className="px-4 pb-3 -mt-1 text-xs text-zinc-500 whitespace-pre-line break-words">{waarde}</p> : null;
  return (
    <div className="px-4 pb-3 -mt-1">
      {open ? (
        <>
          <textarea data-testid={`toelichting-tekst-${taskId}`} aria-label="Toelichting" value={tekst} rows={2} maxLength={MAX}
            onChange={(e) => wijzig(e.target.value)} onBlur={bewaar}
            placeholder="Bijvoorbeeld: do en vr op Elsloo geweest, dus geen 3 locaties"
            className="w-full px-2.5 py-2 rounded-md border border-zinc-300 text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-600" />
          <div data-testid={`toelichting-teken-${taskId}`} className={`text-right font-mono text-[11px] ${tekst.length > MAX ? "text-rose-600 font-bold" : "text-zinc-400"}`}>{tekst.length}/{MAX}</div>
        </>
      ) : (tekst.trim() ? <p data-testid={`toelichting-${taskId}`} className="text-xs text-zinc-500 whitespace-pre-line break-words">{tekst.trim()}</p> : null)}
    </div>
  );
}
